import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector, RootState } from '../../store';
import { fetchTransactions, syncTransactions } from '../../store/actions/FinanceActions';
import Table from '../shared/TailwindComponents/Table';
import Badge from '../shared/TailwindComponents/Badge';
import Dropdown from '../shared/TailwindComponents/Dropdown';
import Button from '../shared/TailwindComponents/Button';
import { PiArrowsClockwiseLight, PiReceiptLight } from 'react-icons/pi';
import { FinanceTransaction } from '../../store/reducer/FinanceReducer';

interface Props {
  organizationId: number | undefined;
}

const formatCurrency = (amount: number, currency: string = 'usd') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
  }).format(amount / 100);
};

const typeLabels: Record<string, string> = {
  payment_received: 'Payment',
  payout: 'Payout',
  refund: 'Refund',
  fee: 'Fee',
  adjustment: 'Adjustment',
};

const typeBadgeColor: Record<string, any> = {
  payment_received: 'green',
  payout: 'blue',
  refund: 'red',
  fee: 'yellow',
  adjustment: 'gray',
};

const SummaryCard: React.FC<{ label: string; value: string; highlight?: boolean }> = ({ label, value, highlight }) => (
  <div className="glass rounded-lg p-4">
    <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
    <p className={`text-lg font-medium mt-1 ${highlight ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-300'}`}>
      {value}
    </p>
  </div>
);

const TransactionsTab: React.FC<Props> = ({ organizationId }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const transactions = useAppSelector((state: RootState) => state.finance.transactions);
  const summary = useAppSelector((state: RootState) => state.finance.summary);
  const loading = useAppSelector((state: RootState) => state.finance.loading);
  const account = useAppSelector((state: RootState) => state.finance.account);
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (organizationId) {
      dispatch(fetchTransactions(organizationId, { page, type: typeFilter || undefined }));
    }
  }, [dispatch, organizationId, page, typeFilter]);

  const handleSync = async () => {
    if (!organizationId) return;
    await dispatch(syncTransactions(organizationId));
    dispatch(fetchTransactions(organizationId, { page: 1, type: typeFilter || undefined }));
    setPage(1);
  };

  const typeFilterOptions = [
    { value: '', label: t('All Types') },
    { value: 'payment_received', label: t('Payments') },
    { value: 'payout', label: t('Payouts') },
    { value: 'refund', label: t('Refunds') },
    { value: 'fee', label: t('Fees') },
  ];

  const columns = ['Date', 'Type', 'Description', 'Amount', 'Net', 'Status'];

  const tableData = transactions.items.map((txn: FinanceTransaction) => ({
    id: txn.id,
    Date: new Date(txn.created_at).toLocaleDateString(),
    Type: txn.type,
    Description: txn.description || '—',
    Amount: txn.amount,
    Net: txn.net_amount,
    Status: txn.status,
    _currency: txn.currency,
    _raw: txn,
  }));

  const renderCell = (column: string, value: any, row: any) => {
    switch (column) {
      case 'Type':
        return (
          <Badge color={typeBadgeColor[value] || 'gray'}>
            {typeLabels[value] || value}
          </Badge>
        );
      case 'Amount':
        return (
          <span className={row._raw.type === 'payment_received' ? 'text-success-600 dark:text-success-400 font-medium' : 'text-gray-700 dark:text-gray-300 font-medium'}>
            {row._raw.type === 'payment_received' ? '+' : ''}{formatCurrency(value, row._currency)}
          </span>
        );
      case 'Net':
        return (
          <span className="text-gray-600 dark:text-gray-400">
            {formatCurrency(value, row._currency)}
          </span>
        );
      case 'Status':
        return (
          <Badge color={value === 'completed' ? 'green' : value === 'pending' ? 'yellow' : 'gray'}>
            {value}
          </Badge>
        );
      default:
        return value;
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-3 gap-4 px-4 pt-4">
          <SummaryCard
            label={t('Total Revenue')}
            value={formatCurrency(summary.total_revenue, summary.currency)}
            highlight
          />
          <SummaryCard
            label={t('Net Balance')}
            value={formatCurrency(summary.net_balance, summary.currency)}
          />
          <SummaryCard
            label={t('Fees')}
            value={formatCurrency(summary.total_fees, summary.currency)}
          />
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center px-4 py-3">
        <Dropdown
          options={typeFilterOptions}
          value={typeFilter}
          onChange={(val) => { setTypeFilter(val as string); setPage(1); }}
          placeholder={t('All Types')}
          compact
          width="w-40"
        />
      </div>

      {/* Table */}
      <Table
        columns={columns}
        data={tableData}
        renderCell={renderCell}
        emptyState={{
          icon: <PiReceiptLight className="w-10 h-10" />,
          headline: t('No transactions yet'),
        }}
      />

      {/* Pagination */}
      {transactions.total_pages > 1 && (
        <div className="flex items-center justify-between px-4 py-3">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('Page')} {transactions.page} {t('of')} {transactions.total_pages} · {transactions.total} {t('total')}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="small"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              {t('Previous')}
            </Button>
            <Button
              variant="outline"
              size="small"
              onClick={() => setPage((p) => Math.min(transactions.total_pages, p + 1))}
              disabled={page >= transactions.total_pages}
            >
              {t('Next')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionsTab;
